// apps/lk/src/components/InsightsBlock.tsx
'use client';
import {
  domainInsight,
  derivedInsight,
  topStrengthsAndGrowth,
} from '@/lib/insights';
import { DiscMatrix } from './DiscMatrix';

/**
 * InsightsBlock — инсайты + баллы.
 * Каждый блок: заголовок → мини-бар с % → текст-инсайт.
 */

type Props = { dashboard: any };

/** Мини-бар с процентом */
function ScoreBar({ label, value, max = 100, color = 'indigo' }: { label: string; value: number; max?: number; color?: string }) {
  const pct = Math.round((value / max) * 100);
  const colorMap: Record<string, string> = {
    indigo: 'bg-indigo-400',
    emerald: 'bg-emerald-400',
    amber: 'bg-amber-400',
    slate: 'bg-slate-400',
  };
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between">
        <span className="text-xs font-medium text-slate-600">{label}</span>
        <span className="text-xs tabular-nums text-slate-400">{pct}%</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-200">
        <div
          className={`h-full rounded-full ${colorMap[color] || colorMap.indigo}`}
          style={{ width: `${Math.max(4, Math.min(100, pct))}%` }}
        />
      </div>
    </div>
  );
}

export function InsightsBlock({ dashboard }: Props) {
  const { bigFive, disc, eq, schwartz } = dashboard;
  const derived = bigFive?.derived ?? {};
  const { strengths, growth } = topStrengthsAndGrowth(derived);

  return (
    <div className="space-y-8">
      {/* ══ DERIVED: сильные стороны + зоны роста (с баллами) ══ */}
      {(strengths.length > 0 || growth.length > 0) && (
        <div className="rounded-3xl border border-slate-200 bg-white p-5 sm:p-6 shadow-lg shadow-slate-200/50">
          <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
            Ваш потенциал
          </h3>
          <div className="space-y-4">
            {strengths.map((s, i) => {
              const rawVal = Object.entries(derived).find(
                ([k]) => derivedLabelMap[k] === s.label,
              );
              const val = rawVal ? Number(rawVal[1]) : null;
              return (
                <div key={`s${i}`}>
                  {val != null && (
                    <div className="mb-2">
                      <ScoreBar label={s.label} value={val} color="emerald" />
                    </div>
                  )}
                  <p className="text-sm leading-relaxed text-slate-600">{s.text}</p>
                </div>
              );
            })}
            {growth.map((g, i) => {
              const rawVal = Object.entries(derived).find(
                ([k]) => derivedLabelMap[k] === g.label,
              );
              const val = rawVal ? Number(rawVal[1]) : null;
              return (
                <div key={`g${i}`} className="border-t border-slate-100 pt-4">
                  {val != null && (
                    <div className="mb-2">
                      <ScoreBar label={g.label} value={val} color="amber" />
                    </div>
                  )}
                  <p className="text-sm leading-relaxed text-slate-600">{g.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ══ DISC: новый DiscMatrix ══ */}
      <DiscMatrix disc={disc} />

      {/* ══ EQ (вынесен в отдельный блок EqBlock на my-profile) ══ */}

    </div>
  );
}

// ── Хелперы ──
const derivedLabelMap: Record<string, string> = {
  leadership: 'Лидерство',
  stressResistance: 'Стрессоустойчивость',
  creativity: 'Креативность',
  discipline: 'Дисциплина',
  socialSkills: 'Социальные навыки',
  empathy: 'Эмпатия',
  businessReliability: 'Деловая надёжность',
};


